// userTracking.ts - Firebase Analytics with uid-based user identification
//
// KEY PRINCIPLES:
// 1. GA4 tracks "Active Users" automatically — no custom event needed
// 2. setUserId uses user.id (uid) from auth system — not PII, no hashing needed
// 3. setUserId must be called BEFORE any events are logged
// 4. Events fired before user identification are queued and replayed
// 5. User properties are set for filtering in Firebase Console

import { IUserProfile } from "@home-health-notify/domain";
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { analyticsConfig } from "./analyticsConfig";
import { getFirebaseAnalytics } from "./firebase";

type Platform = "mobile" | "tablet" | "desktop";

class UserTracking {
  private userId: string | null = null;
  private sessionId: string | null = null;
  private isUserIdentified = false;
  private pendingEvents: Array<{
    name: string;
    params?: Record<string, any>;
  }> = [];
  private featureStartTimes = new Map<string, number>();
  private messageStartTimes = new Map<string, number>();
  private messageEditStartTimes = new Map<string, number>();
  private currentViewport: string | null = null;
  private resizeListener: (() => void) | null = null;

  // ──────────────────────────────────────────────
  // CORE: Event Logging with Queue
  // ──────────────────────────────────────────────

  private trackEvent(eventName: string, params?: Record<string, any>): void {
    if (!this.isTrackingEnabled()) {
      if (analyticsConfig.debug) {
        console.log(`[Analytics] [DEV] ${eventName}`, params);
      }
      return;
    }

    if (!this.isUserIdentified) {
      if (analyticsConfig.debug) {
        console.log(`[Analytics] Queued: ${eventName}`);
      }
      this.pendingEvents.push({ name: eventName, params });
      return;
    }

    const analytics = getFirebaseAnalytics();
    if (analytics) {
      const eventParams = analyticsConfig.debug
        ? { ...params, debug_mode: true }
        : params;

      if (analyticsConfig.debug) {
        console.log(`[Analytics] ${eventName}`, eventParams);
      }

      logEvent(analytics, eventName, eventParams);
    }
  }

  private flushPendingEvents(): void {
    if (this.pendingEvents.length === 0) return;

    if (analyticsConfig.debug) {
      console.log(
        `[Analytics] Flushing ${this.pendingEvents.length} queued events`,
      );
    }

    const analytics = getFirebaseAnalytics();
    for (const { name, params } of this.pendingEvents) {
      if (analytics) {
        const eventParams = analyticsConfig.debug
          ? { ...params, debug_mode: true }
          : params;
        logEvent(analytics, name, eventParams);
      }
    }

    this.pendingEvents = [];
  }

  // ──────────────────────────────────────────────
  // CORE: User Identification
  // ──────────────────────────────────────────────

  public initializeUser(
    userProfile: IUserProfile,
    organizationId?: string,
  ): void {
    if (!userProfile.id) {
      console.warn("[Analytics] No user id, skipping initialization");
      return;
    }

    // Reset any previous user state (shared device support)
    this.reset();

    this.userId = userProfile.id;
    this.sessionId = this.generateSessionId();

    const analytics = getFirebaseAnalytics();
    if (this.isTrackingEnabled() && analytics) {
      // STEP 1: Set user ID FIRST — using uid from auth system (not email)
      setUserId(analytics, this.userId);

      // STEP 2: Set user properties for Console filtering
      setUserProperties(analytics, {
        user_type: userProfile.type || "unknown",
        user_name: userProfile.name || "unknown",
        organization_id: organizationId || "unknown",
        platform: this.getPlatform(),
        screen_resolution: this.getScreenResolution(),
        viewport_size: this.getViewportSize(),
      });

      if (analyticsConfig.debug) {
        console.log(`[Analytics] User identified: ${this.userId}`);
      }
    } else if (analyticsConfig.debug) {
      console.log(`[Analytics] [DEV] User: ${this.userId}`);
    }

    // Mark identified BEFORE any events
    this.isUserIdentified = true;

    // Log session start
    this.trackEvent("session_start_identified", {
      user_id: this.userId,
      session_id: this.sessionId,
      user_type: userProfile.type || "unknown",
      timestamp: new Date().toISOString(),
    });

    // Flush any events that were queued before identification
    this.flushPendingEvents();

    // Start viewport tracking
    this.currentViewport = this.getViewportSize();
    this.startViewportTracking();
  }

  private reset(): void {
    this.userId = null;
    this.sessionId = null;
    this.isUserIdentified = false;
    this.pendingEvents = [];
    this.featureStartTimes.clear();
    this.messageStartTimes.clear();
    this.stopViewportTracking();
  }

  // ──────────────────────────────────────────────
  // SESSION MANAGEMENT
  // ──────────────────────────────────────────────

  public endSession(): void {
    if (!this.isUserIdentified) return;

    this.trackEvent("session_end_identified", {
      user_id: this.userId,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
    });

    // Clear user identity for shared device scenarios
    const analytics = getFirebaseAnalytics();
    if (this.isTrackingEnabled() && analytics) {
      setUserId(analytics, "" as any);
    }

    this.reset();
  }

  // ──────────────────────────────────────────────
  // FEATURE TRACKING
  // ──────────────────────────────────────────────

  public trackFeatureOpen(featureName: string, source: string): void {
    if (!this.isUserIdentified) return;

    const key = `${featureName}_${source}`;
    this.featureStartTimes.set(key, Date.now());

    this.trackEvent(`${featureName}_opened`, {
      user_id: this.userId,
      session_id: this.sessionId,
      source,
      timestamp: new Date().toISOString(),
    });
  }

  public trackFeatureClose(featureName: string, source: string): void {
    if (!this.isUserIdentified) return;

    const key = `${featureName}_${source}`;
    const startTime = this.featureStartTimes.get(key);
    let durationSeconds = 0;

    if (startTime) {
      durationSeconds = Math.floor((Date.now() - startTime) / 1000);
      this.featureStartTimes.delete(key);
    }

    this.trackEvent(`${featureName}_closed`, {
      user_id: this.userId,
      session_id: this.sessionId,
      source,
      duration_seconds: durationSeconds,
      timestamp: new Date().toISOString(),
    });
  }

  // ──────────────────────────────────────────────
  // UI INTERACTION TRACKING
  // ──────────────────────────────────────────────

  public trackSpeedDialAction(actionName: string, chatType?: string): void {
    if (!this.isUserIdentified) return;

    this.trackEvent("speed_dial_action", {
      user_id: this.userId,
      session_id: this.sessionId,
      action: actionName.toLowerCase().replace(/\s+/g, "_"),
      action_label: actionName,
      chat_type: chatType || "unknown",
      timestamp: new Date().toISOString(),
    });
  }

  public trackTabSwitch(
    tabType: string,
    tabLabel: string,
    tabValue: number,
  ): void {
    if (!this.isUserIdentified) return;

    this.trackEvent("tab_switched", {
      user_id: this.userId,
      session_id: this.sessionId,
      tab_type: tabType,
      tab_label: tabLabel,
      tab_value: tabValue,
      timestamp: new Date().toISOString(),
    });
  }

  // ──────────────────────────────────────────────
  // CONNECTIVITY TRACKING
  // ──────────────────────────────────────────────

  public trackOfflineScreen(): void {
    if (!this.isUserIdentified) return;

    this.trackEvent("offline_screen_viewed", {
      user_id: this.userId,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
    });
  }

  public trackBackOnline(offlineDurationSeconds?: number): void {
    if (!this.isUserIdentified) return;

    this.trackEvent("back_online", {
      user_id: this.userId,
      session_id: this.sessionId,
      offline_duration_seconds: offlineDurationSeconds,
      timestamp: new Date().toISOString(),
    });
  }

  // ──────────────────────────────────────────────
  // MESSAGE PERFORMANCE TRACKING
  // ──────────────────────────────────────────────

  public trackMessageSendStart(
    messageId: string,
    chatType?: string,
    startTime?: number,
  ): void {
    if (!this.isUserIdentified) return;

    this.messageStartTimes.set(messageId, startTime ?? Date.now());

    if (analyticsConfig.debug) {
      console.log(`[Analytics] Message send started: ${messageId}`);
    }
  }

  public trackMessageSendComplete(
    messageId: string,
    chatType?: string,
    messageType?: string,
  ): void {
    if (!this.isUserIdentified) return;

    const startTime = this.messageStartTimes.get(messageId);
    if (!startTime) {
      if (analyticsConfig.debug) {
        console.warn(
          `[Analytics] No start time found for message: ${messageId}`,
        );
      }
      return;
    }

    const durationMs = Date.now() - startTime;
    this.messageStartTimes.delete(messageId);

    this.trackEvent("message_sent", {
      user_id: this.userId,
      session_id: this.sessionId,
      message_id: messageId,
      duration_ms: durationMs,
      chat_type: chatType || "unknown",
      message_type: messageType || "text",
      timestamp: new Date().toISOString(),
    });
  }

  public trackMessageSendFailed(
    messageId: string,
    errorReason?: string,
    chatType?: string,
  ): void {
    if (!this.isUserIdentified) return;

    const startTime = this.messageStartTimes.get(messageId);
    const durationMs = startTime ? Date.now() - startTime : 0;
    this.messageStartTimes.delete(messageId);

    this.trackEvent("message_send_failed", {
      user_id: this.userId,
      session_id: this.sessionId,
      message_id: messageId,
      duration_ms: durationMs,
      error_reason: errorReason || "unknown",
      chat_type: chatType || "unknown",
      timestamp: new Date().toISOString(),
    });
  }

  // ──────────────────────────────────────────────
  // MESSAGE EDIT PERFORMANCE TRACKING
  // ──────────────────────────────────────────────
  public trackMessageEditStart(editTrackingId: string): void {
    if (!this.isUserIdentified) return;

    this.messageEditStartTimes.set(editTrackingId, Date.now());

    if (analyticsConfig.debug) {
      console.log(`[Analytics] Message edit started: ${editTrackingId}`);
    }
  }

  public trackMessageEditComplete(
    editTrackingId: string,
    chatType?: string,
  ): void {
    if (!this.isUserIdentified) return;

    const startTime = this.messageEditStartTimes.get(editTrackingId);
    const durationMs = startTime ? Date.now() - startTime : 0;

    this.messageEditStartTimes.delete(editTrackingId);

    this.trackEvent("message_edited", {
      user_id: this.userId,
      session_id: this.sessionId,
      edit_tracking_id: editTrackingId,
      duration_ms: durationMs,
      chat_type: chatType || "unknown",
      timestamp: new Date().toISOString(),
    });
  }

  public trackMessageEditFailed(
    editTrackingId: string,
    errorReason?: string,
    chatType?: string,
  ): void {
    if (!this.isUserIdentified) return;

    const startTime = this.messageEditStartTimes.get(editTrackingId);
    const durationMs = startTime ? Date.now() - startTime : 0;

    this.messageEditStartTimes.delete(editTrackingId);

    this.trackEvent("message_edit_failed", {
      user_id: this.userId,
      session_id: this.sessionId,
      edit_tracking_id: editTrackingId,
      duration_ms: durationMs,
      error_reason: errorReason || "unknown",
      chat_type: chatType || "unknown",
      timestamp: new Date().toISOString(),
    });
  }
  // ──────────────────────────────────────────────
  // VIEWPORT TRACKING
  // ──────────────────────────────────────────────

  private startViewportTracking(): void {
    if (typeof window === "undefined") return;

    let resizeTimeout: NodeJS.Timeout;

    this.resizeListener = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const newViewport = this.getViewportSize();

        if (
          this.currentViewport &&
          newViewport !== this.currentViewport &&
          this.isSignificantChange(this.currentViewport, newViewport)
        ) {
          this.trackEvent("viewport_resized", {
            user_id: this.userId,
            session_id: this.sessionId,
            old_viewport: this.currentViewport,
            new_viewport: newViewport,
            timestamp: new Date().toISOString(),
          });
        }

        this.currentViewport = newViewport;
      }, 1000);
    };

    window.addEventListener("resize", this.resizeListener);
  }

  public stopViewportTracking(): void {
    if (typeof window !== "undefined" && this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
      this.resizeListener = null;
    }
  }

  private isSignificantChange(oldVp: string, newVp: string): boolean {
    const [ow, oh] = oldVp.split("x").map(Number);
    const [nw, nh] = newVp.split("x").map(Number);
    if ([ow, oh, nw, nh].some(isNaN)) return false;
    return Math.abs(nw - ow) > 100 || Math.abs(nh - oh) > 100;
  }

  // ──────────────────────────────────────────────
  // HELPERS
  // ──────────────────────────────────────────────

  private generateSessionId(): string {
    const userPart = this.userId ? this.userId.slice(0, 8) : "anon";
    return `${userPart}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  private isTrackingEnabled(): boolean {
    return analyticsConfig.enabled;
  }

  private getPlatform(): Platform {
    const ua = navigator.userAgent;
    if (/mobile/i.test(ua)) return "mobile";
    if (/tablet/i.test(ua)) return "tablet";
    return "desktop";
  }

  private getScreenResolution(): string {
    return typeof window !== "undefined"
      ? `${window.screen.width}x${window.screen.height}`
      : "unknown";
  }

  private getViewportSize(): string {
    return typeof window !== "undefined"
      ? `${window.innerWidth}x${window.innerHeight}`
      : "unknown";
  }

  // ──────────────────────────────────────────────
  // PUBLIC GETTERS
  // ──────────────────────────────────────────────

  public getEnvironment(): string {
    return analyticsConfig.enabled ? "production" : "development";
  }

  public isTracking(): boolean {
    return this.isTrackingEnabled() && this.isUserIdentified;
  }
}

export const userTracking = new UserTracking();
